/**
 * Simpatico bikes archive scripts.
 *
 * Dedicated interactions for the /bikes archive so the page does not depend on
 * the generic pages bundle for its core behavior.
 *
 * @package Simpaticobike
 */

(function() {
	'use strict';

	function initScrollAnimations() {
		const fadeElements = document.querySelectorAll('.simpatico-fade-in');

		if (!fadeElements.length) return;

		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (prefersReducedMotion) {
			fadeElements.forEach((element) => element.classList.add('is-visible'));
			return;
		}

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.classList.add('is-visible');
					observer.unobserve(entry.target);
				}
			});
		}, {
			root: null,
			rootMargin: '0px 0px -50px 0px',
			threshold: 0.1
		});

		fadeElements.forEach((element) => observer.observe(element));
	}

	function initBikesOverviewV2() {
		const root = document.querySelector('.simpatico-bikes-archive-v2');
		if (!root) return;

		const dataEl = document.getElementById('simpatico-bikes-v2-data');
		const swatchesEl = document.getElementById('simpatico-bikes-v2-shared-swatches');
		const n1FinishEl = document.getElementById('simpatico-bikes-v2-n1-finish');
		const statusEl = document.getElementById('simpatico-bikes-v2-status');

		if (!dataEl || !swatchesEl || !statusEl) {
			return;
		}

		let payload;
		try {
			payload = JSON.parse(dataEl.textContent || '{}');
		} catch (error) {
			console.error('Failed to parse bikes overview data:', error);
			return;
		}

		const models = Array.isArray(payload.models) ? payload.models : [];
		const comparisonPalette = Array.isArray(payload.comparison_palette) ? payload.comparison_palette : [];

		if (!models.length || !comparisonPalette.length) {
			return;
		}

		const state = {
			activeComparisonKey: payload.default_comparison_key || comparisonPalette[0].key || '',
			n1Finish: payload.default_n1_finish || 'brushed'
		};

		const normalizeText = (value) => String(value || '').trim();
		const escapeHtml = (value) => String(value || '')
			.replaceAll('&', '&amp;')
			.replaceAll('<', '&lt;')
			.replaceAll('>', '&gt;')
			.replaceAll('"', '&quot;')
			.replaceAll("'", '&#39;');
		const normalizeHexColor = (value) => /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || '').trim())
			? String(value || '').trim()
			: '#444444';

		const getComparisonLabel = (key) => {
			const match = comparisonPalette.find((entry) => entry.key === key);
			return normalizeText(match?.label || '');
		};

		const getImageMapForModel = (model) => {
			if (!model) {
				return {};
			}

			if (model.id === 'n1' && model.comparison_images_by_finish) {
				const finishMap = model.comparison_images_by_finish[state.n1Finish];
				if (finishMap && typeof finishMap === 'object') {
					return finishMap;
				}
			}

			return model.comparison_images && typeof model.comparison_images === 'object'
				? model.comparison_images
				: {};
		};

		const getSrcsetMapForModel = (model) => {
			if (!model) {
				return {};
			}

			if (model.id === 'n1' && model.comparison_image_srcsets_by_finish) {
				const finishMap = model.comparison_image_srcsets_by_finish[state.n1Finish];
				if (finishMap && typeof finishMap === 'object') {
					return finishMap;
				}
			}

			return model.comparison_image_srcsets && typeof model.comparison_image_srcsets === 'object'
				? model.comparison_image_srcsets
				: {};
		};

		const getResolvedKeyForModel = (model, requestedKey) => {
			const imageMap = getImageMapForModel(model);
			if (!model || !imageMap || !Object.keys(imageMap).length) {
				return '';
			}

			if (requestedKey && imageMap[requestedKey]) {
				return requestedKey;
			}

			if (model.comparison_default_key && imageMap[model.comparison_default_key]) {
				return model.comparison_default_key;
			}

			return Object.keys(imageMap)[0] || '';
		};

		const announceState = () => {
			const activeLabel = getComparisonLabel(state.activeComparisonKey);
			const modelSummary = models.map((model) => {
				const resolvedKey = getResolvedKeyForModel(model, state.activeComparisonKey);
				const modelColour = normalizeText(model?.comparison_color_names?.[resolvedKey] || activeLabel);
				if (model.id === 'n1') {
					return `${model.name} ${state.n1Finish} ${modelColour}`;
				}
				return `${model.name} ${modelColour}`;
			}).join('. ');

			statusEl.textContent = `Color ${activeLabel}. ${modelSummary}.`;
		};

		const updateImages = () => {
			models.forEach((model) => {
				const bikeEl = root.querySelector(`[data-model-id="${model.id}"]`);
				if (!bikeEl) return;

				const imageEl = bikeEl.querySelector('[data-model-image]');
				const resolvedKey = getResolvedKeyForModel(model, state.activeComparisonKey);
				const imageMap = getImageMapForModel(model);
				const srcsetMap = getSrcsetMapForModel(model);
				const imageUrl = imageMap?.[resolvedKey] || '';
				const imageSrcset = srcsetMap?.[resolvedKey] || '';
				const modelColour = normalizeText(model?.comparison_color_names?.[resolvedKey] || getComparisonLabel(resolvedKey));
				const finishLabel = model.id === 'n1' ? `${state.n1Finish} ` : '';

				if (imageEl && imageUrl) {
					imageEl.src = imageUrl;
					if (imageSrcset) {
						imageEl.srcset = imageSrcset;
						imageEl.sizes = '(max-width: 767px) 286px, 33vw';
					} else {
						imageEl.removeAttribute('srcset');
						imageEl.removeAttribute('sizes');
					}
					imageEl.alt = `${model.name} in ${finishLabel}${modelColour}`;
				}
			});
		};

		const updateSwatches = () => {
			swatchesEl.querySelectorAll('[data-comparison-key]').forEach((button) => {
				const isActive = button.dataset.comparisonKey === state.activeComparisonKey;
				button.classList.toggle('is-active', isActive);
				button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
			});
		};

		const updateN1FinishToggle = () => {
			if (!n1FinishEl) {
				return;
			}

			n1FinishEl.querySelectorAll('[data-n1-finish]').forEach((button) => {
				const isActive = button.dataset.n1Finish === state.n1Finish;
				button.classList.toggle('is-active', isActive);
				button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
			});
		};

		const setActiveComparisonKey = (key) => {
			const nextKey = normalizeText(key);
			if (!nextKey || nextKey === state.activeComparisonKey) {
				return;
			}

			state.activeComparisonKey = nextKey;
			updateSwatches();
			updateImages();
			announceState();
		};

		const setN1Finish = (finish) => {
			const nextFinish = normalizeText(finish).toLowerCase();
			if (!['brushed', 'coated'].includes(nextFinish)) {
				return;
			}

			if (state.n1Finish === nextFinish) {
				return;
			}

			state.n1Finish = nextFinish;
			updateN1FinishToggle();
			updateImages();
			announceState();
		};

		swatchesEl.innerHTML = '';
		comparisonPalette.forEach((entry) => {
			const button = document.createElement('button');
			const isActive = entry.key === state.activeComparisonKey;

			button.type = 'button';
			button.className = `simpatico-bikes-v2__shared-swatch${isActive ? ' is-active' : ''}`;
			button.dataset.comparisonKey = String(entry.key || '').trim();
			button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
			button.style.setProperty('--swatch-color', normalizeHexColor(entry.hex));

			const dot = document.createElement('span');
			dot.className = 'simpatico-bikes-v2__shared-swatch-dot';
			dot.setAttribute('aria-hidden', 'true');

			const text = document.createElement('span');
			text.className = 'simpatico-bikes-v2__shared-swatch-text';
			text.textContent = String(entry.label || '');

			button.appendChild(dot);
			button.appendChild(text);
			swatchesEl.appendChild(button);
		});

		swatchesEl.querySelectorAll('[data-comparison-key]').forEach((button) => {
			button.addEventListener('click', () => {
				setActiveComparisonKey(button.dataset.comparisonKey || '');
			});
		});

		if (n1FinishEl) {
			n1FinishEl.querySelectorAll('[data-n1-finish]').forEach((button) => {
				button.addEventListener('click', () => {
					setN1Finish(button.dataset.n1Finish || '');
				});
			});
		}

		updateSwatches();
		updateN1FinishToggle();
		updateImages();
		announceState();
	}

	function init() {
		initScrollAnimations();
		initBikesOverviewV2();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
